/**
 * lib/messageMarkdown — shared parser for the canonical chat message-markdown
 * flavor defined in ADR-0044.
 *
 * Both the composer serializer (KAP-302+) and `MarkdownRenderer` (today and
 * post-KAP-303) MUST import the regexes and helpers from this module. Drift
 * between producer and consumer is impossible by construction when both sides
 * share these primitives.
 *
 * v1 scope (KAP-301): regex constants + parse/serialize helpers for the
 * mention and channel sentinels, plus a pragmatic `isInsideCode` walker so
 * the renderer can skip mention parsing inside code spans / fences. KAP-302
 * and KAP-303 wire these into Lexical and `MarkdownRenderer`.
 */

const UUID_GROUP = '[0-9a-fA-F-]{36}';
const LABEL_GROUP = '(?:\\\\\\]|[^\\]])+';

/**
 * Global regex for finding every mention sentinel in a haystack string.
 *
 * Capture groups: [1] display name (still containing any `\]` escapes —
 * call `parseMention(match[0])` or unescape `\]` → `]` before display),
 * [2] user UUID.
 *
 * NOTE: applying this to raw source text will also match sentinels that
 * appear inside code spans / fences. Renderer callers MUST guard with
 * `isInsideCode(source, matchIndex)` per ADR-0044 §2.
 */
export const MENTION_SENTINEL_REGEX = new RegExp(
  `@\\[(${LABEL_GROUP})\\]\\(user:(${UUID_GROUP})\\)`,
  'g'
);

/**
 * Global regex for the reserved channel sentinel form. No feature consumes
 * this today; shipped here so a future #channel feature does not reopen
 * ADR-0044.
 */
export const CHANNEL_SENTINEL_REGEX = new RegExp(
  `#\\[(${LABEL_GROUP})\\]\\(channel:(${UUID_GROUP})\\)`,
  'g'
);

const SINGLE_MENTION_REGEX = new RegExp(
  `^@\\[(${LABEL_GROUP})\\]\\(user:(${UUID_GROUP})\\)$`
);

const SINGLE_CHANNEL_REGEX = new RegExp(
  `^#\\[(${LABEL_GROUP})\\]\\(channel:(${UUID_GROUP})\\)$`
);

function unescapeLabel(label) {
  return label.replace(/\\\]/g, ']');
}

function escapeLabel(label) {
  return String(label).replace(/\]/g, '\\]');
}

/**
 * Parse a single mention sentinel string.
 * Returns `{ displayName, userId }` on success, or `null` if the input is
 * not a well-formed mention sentinel.
 */
export function parseMention(input) {
  if (typeof input !== 'string') return null;
  const match = input.match(SINGLE_MENTION_REGEX);
  if (!match) return null;
  return {
    displayName: unescapeLabel(match[1]),
    userId: match[2]
  };
}

/**
 * Serialize a mention to the canonical sentinel form. `displayName` is
 * escape-encoded (literal `]` → `\]`).
 */
export function serializeMention({ displayName, userId }) {
  if (typeof displayName !== 'string' || typeof userId !== 'string') {
    throw new TypeError('serializeMention requires { displayName, userId } strings');
  }
  return `@[${escapeLabel(displayName)}](user:${userId})`;
}

/**
 * Parse a single channel sentinel string. Reserved; no feature consumes
 * this today.
 */
export function parseChannel(input) {
  if (typeof input !== 'string') return null;
  const match = input.match(SINGLE_CHANNEL_REGEX);
  if (!match) return null;
  return {
    channelName: unescapeLabel(match[1]),
    channelId: match[2]
  };
}

/**
 * Serialize a channel reference to the canonical sentinel form.
 */
export function serializeChannel({ channelName, channelId }) {
  if (typeof channelName !== 'string' || typeof channelId !== 'string') {
    throw new TypeError('serializeChannel requires { channelName, channelId } strings');
  }
  return `#[${escapeLabel(channelName)}](channel:${channelId})`;
}

/**
 * Returns true if `targetIndex` in `source` falls inside an inline code span
 * or a fenced code block, per CommonMark — meaning a mention sentinel at that
 * position MUST render as literal text rather than as a mention.
 *
 * This is a pragmatic walker, not a complete CommonMark tokenizer. It tracks:
 *   - fenced code blocks opened by a run of 3+ backticks at line start
 *     (with up to 3 leading spaces), toggled by a matching closer on its
 *     own line;
 *   - inline code spans toggled by single backticks outside a fence.
 *
 * It deliberately does NOT handle multi-char inline code delimiters
 * (`` `` `` … `` `` ``), indented code blocks, or backslash-escaped
 * backticks. The renderer (KAP-303) operates on the post-`remark-gfm` AST
 * rather than raw source for those cases; this helper exists for the
 * narrower job of guarding raw-source consumers.
 */
export function isInsideCode(source, targetIndex) {
  if (typeof source !== 'string') return false;
  if (typeof targetIndex !== 'number' || targetIndex < 0) return false;
  const limit = Math.min(targetIndex, source.length);
  let i = 0;
  let inFence = false;
  let inInlineCode = false;
  let atLineStart = true;
  while (i < limit) {
    if (atLineStart) {
      let j = i;
      let leadingSpaces = 0;
      while (j < source.length && source[j] === ' ' && leadingSpaces < 3) {
        j += 1;
        leadingSpaces += 1;
      }
      let bt = 0;
      while (j < source.length && source[j] === '`') {
        j += 1;
        bt += 1;
      }
      if (bt >= 3) {
        inFence = !inFence;
        inInlineCode = false;
        i = j;
        while (i < source.length && source[i] !== '\n') i += 1;
        atLineStart = false;
        continue;
      }
    }
    const ch = source[i];
    if (ch === '\n') {
      atLineStart = true;
      i += 1;
      continue;
    }
    atLineStart = false;
    if (!inFence && ch === '`') {
      inInlineCode = !inInlineCode;
    }
    i += 1;
  }
  return inFence || inInlineCode;
}
